import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Activity, BarChart3, Eye, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdvancedTradingChart = () => {
  const [ohlcvData, setOhlcvData] = useState(null);
  const [externalSignals, setExternalSignals] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState('15min');
  const [showIndicators, setShowIndicators] = useState({ ema20: true, ema50: true, ema200: false, rsi: true });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('candlestick'); // 'candlestick', 'tradingview', 'signals'
  const canvasRef = useRef(null);

  const intervals = [
    { value: '5min', label: '5m' },
    { value: '15min', label: '15m' },
    { value: '1hour', label: '1H' },
    { value: '4hour', label: '4H' },
    { value: '1day', label: '1D' },
  ];

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, [selectedInterval]);

  useEffect(() => {
    if (ohlcvData && canvasRef.current && activeTab === 'candlestick') {
      drawCandlestickChart();
    }
  }, [ohlcvData, showIndicators, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ohlcvRes, signalsRes] = await Promise.all([
        axios.get(`${API}/market/ohlcv?interval=${selectedInterval}&limit=100`),
        axios.get(`${API}/signals/external`)
      ]);
      setOhlcvData(ohlcvRes.data);
      setExternalSignals(signalsRes.data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
    setLoading(false);
  };

  const drawCandlestickChart = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const candles = ohlcvData?.candles || [];
    
    if (candles.length === 0) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);

    const width = rect.width;
    const height = rect.height;
    const padding = { top: 20, right: 60, bottom: 40, left: 10 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Clear canvas
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, width, height);

    // Calculate price range
    const prices = candles.flatMap(c => [c.high, c.low]);
    const minPrice = Math.min(...prices) * 0.999;
    const maxPrice = Math.max(...prices) * 1.001;
    const priceRange = maxPrice - minPrice;

    // Helper functions
    const xScale = (i) => padding.left + (i / candles.length) * chartWidth;
    const yScale = (price) => padding.top + (1 - (price - minPrice) / priceRange) * chartHeight;

    // Draw grid
    ctx.strokeStyle = '#2a2a4a';
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (i / 5) * chartHeight;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();

      // Price labels
      const price = maxPrice - (i / 5) * priceRange;
      ctx.fillStyle = '#888';
      ctx.font = '10px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`$${price.toFixed(2)}`, width - padding.right + 5, y + 3);
    }

    // Draw candlesticks
    const candleWidth = Math.max(2, (chartWidth / candles.length) * 0.7);
    candles.forEach((candle, i) => {
      const x = xScale(i);
      const isGreen = candle.close >= candle.open;
      
      // Wick
      ctx.strokeStyle = isGreen ? '#00ff88' : '#ff4466';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x + candleWidth / 2, yScale(candle.high));
      ctx.lineTo(x + candleWidth / 2, yScale(candle.low));
      ctx.stroke();

      // Body
      ctx.fillStyle = isGreen ? '#00ff88' : '#ff4466';
      const bodyTop = yScale(Math.max(candle.open, candle.close));
      const bodyBottom = yScale(Math.min(candle.open, candle.close));
      const bodyHeight = Math.max(1, bodyBottom - bodyTop);
      ctx.fillRect(x, bodyTop, candleWidth, bodyHeight);
    });

    // Draw EMAs
    const drawLine = (data, color, key) => {
      const validPoints = candles.map((c, i) => ({ x: xScale(i) + candleWidth / 2, y: c[key] ? yScale(c[key]) : null }))
        .filter(p => p.y !== null);
      
      if (validPoints.length < 2) return;
      
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      validPoints.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.stroke();
    };

    if (showIndicators.ema20) drawLine(candles, '#ffcc00', 'ema20');
    if (showIndicators.ema50) drawLine(candles, '#00ccff', 'ema50');
    if (showIndicators.ema200) drawLine(candles, '#ff66ff', 'ema200');

    // Draw current price line
    const currentPrice = candles[candles.length - 1]?.close;
    if (currentPrice) {
      const y = yScale(currentPrice);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Price label
      ctx.fillStyle = '#3b82f6';
      ctx.fillRect(width - padding.right, y - 10, 55, 20);
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 10px Arial';
      ctx.fillText(`$${currentPrice.toFixed(2)}`, width - padding.right + 3, y + 4);
    }

    // Time labels
    ctx.fillStyle = '#666';
    ctx.font = '9px Arial';
    ctx.textAlign = 'center';
    const labelStep = Math.ceil(candles.length / 6);
    candles.forEach((candle, i) => {
      if (i % labelStep === 0) {
        const date = new Date(candle.time);
        const label = `${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
        ctx.fillText(label, xScale(i) + candleWidth / 2, height - padding.bottom + 15);
      }
    });
  };

  const SignalCard = ({ signal }) => {
    const bgColor = signal.signal === 'LONG' ? 'bg-green-900/30 border-green-700' : 
                    signal.signal === 'SHORT' ? 'bg-red-900/30 border-red-700' : 
                    'bg-gray-800/50 border-gray-700';
    const textColor = signal.signal === 'LONG' ? 'text-green-400' : 
                      signal.signal === 'SHORT' ? 'text-red-400' : 'text-gray-400';

    return (
      <div className={`p-3 rounded-lg border ${bgColor}`}>
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-semibold text-white">{signal.source}</span>
          <span className={`text-sm font-bold ${textColor}`}>{signal.signal}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-400">{signal.reason}</span>
          <span className="text-xs text-gray-500">{signal.confidence}%</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="flex items-center justify-between bg-gray-800/50 rounded-lg p-2">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('candlestick')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              activeTab === 'candlestick' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <BarChart3 className="inline mr-2" size={16} />
            Candlestick
          </button>
          <button
            onClick={() => setActiveTab('tradingview')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              activeTab === 'tradingview' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Eye className="inline mr-2" size={16} />
            TradingView
          </button>
          <button
            onClick={() => setActiveTab('signals')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${
              activeTab === 'signals' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Activity className="inline mr-2" size={16} />
            AI Signals
          </button>
        </div>
        
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Candlestick Chart Tab */}
      {activeTab === 'candlestick' && (
        <div className="bg-gray-800/50 backdrop-blur rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">📊 ETH/USDT Candlestick Chart</h3>
            <div className="flex items-center gap-4">
              {/* Interval Selector */}
              <div className="flex gap-1">
                {intervals.map(int => (
                  <button
                    key={int.value}
                    onClick={() => setSelectedInterval(int.value)}
                    className={`px-3 py-1 rounded text-xs font-semibold transition ${
                      selectedInterval === int.value
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {int.label}
                  </button>
                ))}
              </div>
              
              {/* Indicator Toggles */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowIndicators(prev => ({ ...prev, ema20: !prev.ema20 }))}
                  className={`px-2 py-1 rounded text-xs font-semibold ${
                    showIndicators.ema20 ? 'bg-yellow-600 text-white' : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  EMA20
                </button>
                <button
                  onClick={() => setShowIndicators(prev => ({ ...prev, ema50: !prev.ema50 }))}
                  className={`px-2 py-1 rounded text-xs font-semibold ${
                    showIndicators.ema50 ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  EMA50
                </button>
                <button
                  onClick={() => setShowIndicators(prev => ({ ...prev, ema200: !prev.ema200 }))}
                  className={`px-2 py-1 rounded text-xs font-semibold ${
                    showIndicators.ema200 ? 'bg-pink-600 text-white' : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  EMA200
                </button>
              </div>
            </div>
          </div>
          
          {/* Canvas Chart */}
          <div className="relative">
            <canvas
              ref={canvasRef}
              className="w-full h-[400px] rounded-lg"
              style={{ background: '#1a1a2e' }}
            />
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
                <RefreshCw className="animate-spin text-purple-500" size={32} />
              </div>
            )}
          </div>
          
          {/* Legend */}
          <div className="flex gap-6 mt-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span className="text-gray-400">Bullish</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span className="text-gray-400">Bearish</span>
            </div>
            {showIndicators.ema20 && (
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 bg-yellow-500"></div>
                <span className="text-gray-400">EMA 20</span>
              </div>
            )}
            {showIndicators.ema50 && (
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 bg-cyan-500"></div>
                <span className="text-gray-400">EMA 50</span>
              </div>
            )}
            {showIndicators.ema200 && (
              <div className="flex items-center gap-2">
                <div className="w-6 h-0.5 bg-pink-500"></div>
                <span className="text-gray-400">EMA 200</span>
              </div>
            )}
          </div>
          
          {/* RSI Indicator */}
          {showIndicators.rsi && ohlcvData?.candles && (
            <div className="mt-4 p-3 bg-gray-900/50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-gray-300">RSI (14)</span>
                <span className={`text-sm font-bold ${
                  ohlcvData.candles[ohlcvData.candles.length - 1]?.rsi > 70 ? 'text-red-400' :
                  ohlcvData.candles[ohlcvData.candles.length - 1]?.rsi < 30 ? 'text-green-400' : 'text-yellow-400'
                }`}>
                  {ohlcvData.candles[ohlcvData.candles.length - 1]?.rsi?.toFixed(1) || 'N/A'}
                </span>
              </div>
              <div className="relative h-2 bg-gray-700 rounded-full">
                <div className="absolute left-[30%] w-px h-full bg-green-500/50"></div>
                <div className="absolute left-[70%] w-px h-full bg-red-500/50"></div>
                <div 
                  className="absolute h-full w-1 bg-white rounded-full transition-all"
                  style={{ left: `${Math.min(100, Math.max(0, ohlcvData.candles[ohlcvData.candles.length - 1]?.rsi || 50))}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-500">
                <span>Oversold (30)</span>
                <span>Overbought (70)</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TradingView Widget Tab */}
      {activeTab === 'tradingview' && (
        <div className="bg-gray-800/50 backdrop-blur rounded-lg p-4 border border-gray-700">
          <h3 className="text-lg font-bold text-white mb-4">📈 TradingView Professional Chart</h3>
          <div className="rounded-lg overflow-hidden" style={{ height: '500px' }}>
            <iframe
              src="https://s.tradingview.com/widgetembed/?frameElementId=tradingview_widget&symbol=KUCOIN%3AETHUSDT&interval=60&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=1a1a2e&studies=%5B%5D&theme=dark&style=1&timezone=exchange&withdateranges=1&showpopupbutton=1&studies_overrides=%7B%7D&overrides=%7B%7D&enabled_features=%5B%5D&disabled_features=%5B%5D&locale=en&utm_source=&utm_medium=widget&utm_campaign=chart"
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="TradingView Chart"
              allowFullScreen
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Powered by TradingView. Use drawing tools, add indicators, and perform in-depth analysis.
          </p>
        </div>
      )}

      {/* AI Signals Tab */}
      {activeTab === 'signals' && (
        <div className="bg-gray-800/50 backdrop-blur rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">🤖 External AI Signals Comparison</h3>
            {externalSignals && (
              <div className={`px-4 py-2 rounded-lg font-bold text-sm ${
                externalSignals.strategy_match 
                  ? 'bg-green-900/50 text-green-400 border border-green-700' 
                  : 'bg-yellow-900/50 text-yellow-400 border border-yellow-700'
              }`}>
                {externalSignals.match_status}
              </div>
            )}
          </div>
          
          {externalSignals && (
            <>
              {/* Consensus Summary */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-gray-900/50 rounded-lg border border-gray-600">
                  <h4 className="text-sm font-semibold text-gray-400 mb-2">External Consensus</h4>
                  <div className="flex items-center gap-4">
                    <div className={`text-3xl font-bold ${
                      externalSignals.consensus.signal === 'LONG' ? 'text-green-400' :
                      externalSignals.consensus.signal === 'SHORT' ? 'text-red-400' : 'text-gray-400'
                    }`}>
                      {externalSignals.consensus.signal}
                    </div>
                    <div className="text-sm text-gray-400">
                      <div>Strength: {externalSignals.consensus.strength}%</div>
                      <div className="flex gap-2 mt-1">
                        <span className="text-green-400">🟢 {externalSignals.consensus.long_count}</span>
                        <span className="text-red-400">🔴 {externalSignals.consensus.short_count}</span>
                        <span className="text-gray-400">⚪ {externalSignals.consensus.neutral_count}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-purple-900/30 rounded-lg border border-purple-700">
                  <h4 className="text-sm font-semibold text-purple-400 mb-2">Your Strategy</h4>
                  <div className="flex items-center gap-4">
                    <div className={`text-3xl font-bold ${
                      externalSignals.our_strategy.signal === 'LONG' ? 'text-green-400' :
                      externalSignals.our_strategy.signal === 'SHORT' ? 'text-red-400' : 'text-gray-400'
                    }`}>
                      {externalSignals.our_strategy.signal}
                    </div>
                    <div className="text-sm text-gray-400">
                      <div>Confidence: {externalSignals.our_strategy.confidence}/100</div>
                      <div>Threshold: {externalSignals.our_strategy.threshold}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Strategy Match Indicator */}
              <div className={`p-4 mb-6 rounded-lg border ${
                externalSignals.strategy_match 
                  ? 'bg-green-900/20 border-green-700' 
                  : 'bg-yellow-900/20 border-yellow-700'
              }`}>
                <div className="flex items-center gap-3">
                  {externalSignals.strategy_match ? (
                    <>
                      <TrendingUp className="text-green-400" size={24} />
                      <div>
                        <p className="font-bold text-green-400">Signals Aligned!</p>
                        <p className="text-sm text-gray-400">External indicators agree with your ETH LONG strategy assessment.</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <TrendingDown className="text-yellow-400" size={24} />
                      <div>
                        <p className="font-bold text-yellow-400">Signal Divergence</p>
                        <p className="text-sm text-gray-400">External signals differ from your strategy. Consider waiting for alignment.</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Individual Signals */}
              <h4 className="text-sm font-semibold text-gray-300 mb-3">Individual Signal Sources</h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {externalSignals.signals.map((signal, idx) => (
                  <SignalCard key={idx} signal={signal} />
                ))}
              </div>
              
              <p className="text-xs text-gray-500 mt-4">
                Last updated: {new Date(externalSignals.timestamp).toLocaleTimeString()} | 
                ETH Price: ${externalSignals.current_price?.toFixed(2)}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdvancedTradingChart;
